import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomButton from "components/common/CustomButton";
import ManagementPageShell from "components/management/ManagementPageShell";
import IntegrationClientSelect from "components/integrations-beta/IntegrationClientSelect";
import ScheduleFormFields, {
  useScheduleForm,
} from "components/integrations-beta/ScheduleFormFields";
import StageChip from "components/integrations-beta/StageChip";
import StepFormDialog, {
  humanizeStepKey,
  type StepFormValues,
} from "components/integrations-beta/StepFormDialog";
import TimezoneSelect from "components/integrations-beta/TimezoneSelect";
import WebhookSetupReveal from "components/integrations-beta/WebhookSetupReveal";
import WebhookTriggerFields from "components/integrations-beta/WebhookTriggerFields";
import Layout from "components/Layout";
import {
  connectionLabel as connectionDisplayLabel,
  useGetConnectionsQuery,
} from "features/integrationsBeta/connections";
import type { IntegrationTriggerMode } from "features/integrationsBeta/integrationsBeta";
import {
  useGetExecutionTargetsQuery,
  usePlatformEnumLabels,
  useGetWebhookProvidersQuery,
} from "features/integrationsBeta/platformEnums";
import {
  ClientItem,
  useCreateIntegrationMutation,
  useCreateStepMutation,
} from "features/integrationsBeta/registration";
import { useCreateScheduleMutation } from "features/integrationsBeta/schedules";
import { useCreateWebhookTriggerMutation } from "features/integrationsBeta/webhookTriggers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { enumLabel } from "features/integrationsBeta/enumLabel";
import { formatApiError } from "utils/formatApiError";

interface DraftStep extends StepFormValues {
  tempId: string;
}

// Connections and credentials are driven by the steps' declared requirements
// (same model as Settings): steps are drafted here, each binding or creating
// its connection inline; the trigger step is derived from the drafts; and
// credentials are configured after creation, guided by the readiness banner.
const STEPS = ["Integration", "Steps", "Trigger", "Review"];

export default function RegistrationWizardPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const idCounter = useRef(0);
  const nextTempId = () => `t${++idCounter.current}`;

  const { data: execTargets = [] } = useGetExecutionTargetsQuery();
  const { data: webhookProviders = [] } = useGetWebhookProvidersQuery();
  const { stageLabel } = usePlatformEnumLabels();

  const [createIntegration] = useCreateIntegrationMutation();
  const [createStep] = useCreateStepMutation();
  const [createSchedule] = useCreateScheduleMutation();
  const [createTrigger] = useCreateWebhookTriggerMutation();

  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{
    integrationId: number;
    stepsCreated: number;
    triggerCreated: boolean;
  } | null>(null);

  // Step 0 - Integration
  const [client, setClient] = useState<ClientItem | null>(null);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [executionTarget, setExecutionTarget] = useState("ECS");
  const [timezone, setTimezone] = useState("America/Chicago");

  // Step 1 - Steps (drafts; connections are real client-scoped rows)
  const [draftSteps, setDraftSteps] = useState<DraftStep[]>([]);
  // null = closed, "add" = add mode, otherwise the tempId being edited
  const [stepDialog, setStepDialog] = useState<string | null>(null);

  // Step 2 - Trigger (derived from the drafted steps)
  const triggerMode: IntegrationTriggerMode = draftSteps.some(
    (s) => s.entry.requires_event_payload,
  )
    ? "WEBHOOK"
    : "SCHEDULE";
  const [webhookName, setWebhookName] = useState("");
  const [webhookProvider, setWebhookProvider] = useState("");
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const scheduleForm = useScheduleForm();

  // Set after a webhook integration is created: show its URL + one-time
  // Secret-Key before leaving the wizard.
  const [webhookResult, setWebhookResult] = useState<{
    integrationId: number;
    url: string;
    secret: string;
  } | null>(null);

  const { data: clientConnections = [] } = useGetConnectionsQuery(
    { client: client?.id ?? 0 },
    { skip: !client },
  );
  const connectionLabel = (id: number | null) => {
    if (id == null) return "—";
    const conn = clientConnections.find((c) => c.id === id);
    return conn ? connectionDisplayLabel(conn) : `#${id}`;
  };

  const handleClientChange = (next: ClientItem | null) => {
    const changed = (next?.id ?? null) !== (client?.id ?? null);
    if (changed && client != null) {
      // Connections are client-scoped: bindings from the old client are void.
      if (draftSteps.some((s) => s.connection != null)) {
        setDraftSteps((rows) => rows.map((r) => ({ ...r, connection: null })));
        dispatch(
          pushNotification({
            severity: "warning",
            message: "Connection selections were reset for the new client.",
          }),
        );
      }
    }
    setClient(next);
  };

  const step0Valid =
    !!client &&
    client.has_backend_user !== false &&
    !!name.trim() &&
    !!timezone.trim();
  const stepsValid = draftSteps.every(
    (s) => !s.entry.connection_requirement || s.connection != null,
  );
  const triggerValid =
    triggerMode === "WEBHOOK"
      ? !!webhookProvider
      : !scheduleEnabled || scheduleForm.isValid;

  const nextDisabled =
    (active === 0 && !step0Valid) ||
    (active === 1 && !stepsValid) ||
    (active === 2 && !triggerValid);

  const nextStageOrder =
    draftSteps.reduce((max, s) => Math.max(max, s.stage_order), 0) + 1;
  const editingDraft =
    stepDialog !== null && stepDialog !== "add"
      ? (draftSteps.find((s) => s.tempId === stepDialog) ?? null)
      : null;

  const handleToggleSchedule = (enabled: boolean) => {
    setScheduleEnabled(enabled);
    if (enabled && !scheduleForm.name.trim()) {
      scheduleForm.setName(name.trim());
    }
  };

  const openSettings = (integrationId: number) =>
    navigate(`/integrations-beta/${integrationId}`, {
      state: { initialTab: "settings" },
    });

  const handleCreate = async () => {
    if (!client) return;
    setCreating(true);
    setCreateError(null);
    let integrationId: number | null = null;
    let stepsCreated = 0;
    let triggerCreated = false;
    try {
      const integration = await createIntegration({
        client: client.id,
        name: name.trim(),
        description: description.trim(),
        execution_target: executionTarget,
        timezone: timezone.trim(),
      }).unwrap();
      integrationId = integration.id;

      const ordered = [...draftSteps].sort(
        (a, b) => a.stage_order - b.stage_order,
      );
      for (const draft of ordered) {
        await createStep({
          integrationId: integration.id,
          stage: draft.stage,
          stage_order: draft.stage_order,
          step_key: draft.entry.step_key,
          name: draft.name,
          is_enabled: true,
          connection: draft.connection,
          config: draft.config,
        }).unwrap();
        stepsCreated += 1;
      }

      if (triggerMode === "WEBHOOK") {
        const trigger = await createTrigger({
          integration: integration.id,
          provider: webhookProvider,
          ...(webhookName.trim() ? { name: webhookName.trim() } : {}),
        }).unwrap();
        dispatch(
          pushNotification({
            severity: "success",
            message: "Integration registered.",
          }),
        );
        // Stop here to show the URL + one-time Secret-Key before leaving.
        setWebhookResult({
          integrationId: integration.id,
          url: trigger.webhook_url,
          secret: trigger.secret_key ?? "",
        });
        return;
      } else if (scheduleEnabled) {
        await createSchedule({
          integration: integration.id,
          display_name: scheduleForm.name.trim(),
          cron_expression: scheduleForm.cron,
        }).unwrap();
        triggerCreated = true;
      }

      dispatch(
        pushNotification({
          severity: "success",
          message: "Integration registered. Settings will guide credentials.",
        }),
      );
      openSettings(integration.id);
    } catch (e) {
      const data = (e as { data?: Record<string, unknown> })?.data;
      const detail = formatApiError(data, "Registration failed.");
      if (integrationId == null) {
        const nameMessages = data && (data.name ?? data.non_field_errors);
        if (nameMessages) {
          setNameError(
            Array.isArray(nameMessages)
              ? nameMessages.join(" ")
              : String(nameMessages),
          );
          setActive(0);
        }
        setCreateError(detail);
      } else {
        setProgress({ integrationId, stepsCreated, triggerCreated });
        setCreateError(detail);
      }
    } finally {
      setCreating(false);
    }
  };

  const triggerSummary =
    triggerMode === "WEBHOOK"
      ? `Webhook: ${
          webhookName.trim() ? `${webhookName.trim()} - ` : ""
        }${enumLabel(webhookProviders, webhookProvider)} (secret auto-generated)`
      : scheduleEnabled
        ? `Schedule: ${scheduleForm.summary}`
        : "Manual runs only";

  return (
    <Layout
      title="Register Integration"
      content="Register Integration"
      noScroll
    >
      <ManagementPageShell
        title="Register New Integration"
        headerExtra={
          <CustomButton
            fullWidth={false}
            onClick={() => navigate("/integrations-beta")}
          >
            Cancel
          </CustomButton>
        }
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Box sx={{ maxWidth: 900, width: "100%", mx: "auto" }}>
            {webhookResult ? (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1 }}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Integration registered
                  </Typography>
                  <WebhookSetupReveal
                    webhookUrl={webhookResult.url}
                    secretKey={webhookResult.secret}
                    oneTime
                  />
                  <Box>
                    <CustomButton
                      fullWidth={false}
                      onClick={() => openSettings(webhookResult.integrationId)}
                    >
                      Open integration settings
                    </CustomButton>
                  </Box>
                </Box>
              </Paper>
            ) : (
              <>
                <Stepper activeStep={active} sx={{ mb: 3, pt: 1 }}>
                  {STEPS.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1 }}>
                  {active === 0 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <IntegrationClientSelect
                        value={client}
                        onChange={handleClientChange}
                      />
                      {client?.has_backend_user === false && (
                        <Alert severity="warning">
                          Client "{client.name}" has no backend user yet —
                          integrations cannot be created for it. Create one in{" "}
                          <Button
                            size="small"
                            onClick={() =>
                              window.open("/user-management", "_blank")
                            }
                          >
                            User Management
                          </Button>
                          (Backend Users tab), then re-select the client.
                        </Alert>
                      )}
                      <TextField
                        size="small"
                        label="Integration name"
                        required
                        value={name}
                        error={!!nameError}
                        helperText={nameError ?? undefined}
                        onChange={(e) => {
                          setName(e.target.value);
                          setNameError(null);
                        }}
                      />
                      <TextField
                        size="small"
                        label="Description"
                        multiline
                        minRows={2}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                      <TextField
                        select
                        size="small"
                        label="Execution target"
                        value={executionTarget}
                        onChange={(e) => setExecutionTarget(e.target.value)}
                      >
                        {execTargets.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TimezoneSelect
                        value={timezone}
                        onChange={setTimezone}
                        label="Timezone"
                      />
                    </Box>
                  )}

                  {active === 1 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          size="small"
                          color={triggerMode === "WEBHOOK" ? "info" : "default"}
                          label={
                            triggerMode === "WEBHOOK"
                              ? "Trigger mode: Webhook-driven"
                              : "Trigger mode: Schedule / manual"
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          Derived from the steps: a step that consumes the
                          webhook payload makes the whole pipeline
                          webhook-triggered.
                        </Typography>
                      </Box>

                      {[...draftSteps]
                        .sort((a, b) => a.stage_order - b.stage_order)
                        .map((draft) => (
                          <Paper
                            key={draft.tempId}
                            variant="outlined"
                            sx={{ p: 1.5 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <StageChip
                                stage={draft.stage}
                                label={stageLabel(draft.stage)}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ flexGrow: 1, minWidth: 0 }}
                                noWrap
                              >
                                {draft.stage_order}. {draft.name}
                              </Typography>
                              {draft.entry.connection_requirement &&
                                draft.connection == null && (
                                  <Chip
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    label="Connection required"
                                  />
                                )}
                              <Tooltip title="Edit step">
                                <IconButton
                                  size="small"
                                  onClick={() => setStepDialog(draft.tempId)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove step">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setDraftSteps((rows) =>
                                      rows.filter(
                                        (r) => r.tempId !== draft.tempId,
                                      ),
                                    )
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "monospace",
                                color: "text.secondary",
                                wordBreak: "break-all",
                              }}
                            >
                              {draft.entry.step_key}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Connection: {connectionLabel(draft.connection)}
                            </Typography>
                          </Paper>
                        ))}

                      {!draftSteps.length && (
                        <Typography variant="body2" color="text.secondary">
                          No steps yet. You can also add steps later in
                          Settings.
                        </Typography>
                      )}

                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setStepDialog("add")}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Add step
                      </Button>
                    </Box>
                  )}

                  {active === 2 && (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {triggerMode === "WEBHOOK" ? (
                        <>
                          <Alert severity="info">
                            This pipeline is webhook-triggered — schedules are
                            not allowed. A Secret-Key is generated
                            automatically; the webhook URL and secret are shown
                            right after creation.
                          </Alert>
                          <WebhookTriggerFields
                            name={webhookName}
                            onNameChange={setWebhookName}
                            provider={webhookProvider}
                            onProviderChange={setWebhookProvider}
                          />
                        </>
                      ) : (
                        <>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={scheduleEnabled}
                                onChange={(_, c) => handleToggleSchedule(c)}
                              />
                            }
                            label="Add a schedule"
                          />
                          {scheduleEnabled ? (
                            <ScheduleFormFields form={scheduleForm} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No schedule — the integration will run on manual
                              triggers only. You can add schedules later in
                              Settings.
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}

                  {active === 3 && (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Review
                      </Typography>
                      <Typography variant="body2">
                        <strong>Integration:</strong> {name} (
                        {client?.name ?? "?"}
                        ), {executionTarget}, {timezone}
                      </Typography>

                      {draftSteps.length ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Stage</TableCell>
                              <TableCell>Order</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Step key</TableCell>
                              <TableCell>Connection</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...draftSteps]
                              .sort((a, b) => a.stage_order - b.stage_order)
                              .map((draft) => (
                                <TableRow key={draft.tempId}>
                                  <TableCell>
                                    {stageLabel(draft.stage)}
                                  </TableCell>
                                  <TableCell>{draft.stage_order}</TableCell>
                                  <TableCell>{draft.name}</TableCell>
                                  <TableCell sx={{ fontFamily: "monospace" }}>
                                    {humanizeStepKey(draft.entry.step_key)}
                                  </TableCell>
                                  <TableCell>
                                    {connectionLabel(draft.connection)}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2">
                          <strong>Steps:</strong> none
                        </Typography>
                      )}

                      <Typography variant="body2">
                        <strong>Trigger:</strong> {triggerSummary}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Credentials are configured after creation — the Settings
                        page will guide you through anything missing.
                      </Typography>

                      {createError && (
                        <Alert
                          severity="error"
                          action={
                            progress && (
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() =>
                                  openSettings(progress.integrationId)
                                }
                              >
                                Open integration settings
                              </Button>
                            )
                          }
                        >
                          {progress &&
                            `Integration #${progress.integrationId} was created; ` +
                              `${progress.stepsCreated} of ${draftSteps.length} steps ` +
                              `created; trigger ${progress.triggerCreated ? "created" : "not created"}. `}
                          {createError}
                        </Alert>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Button
                      disabled={active === 0 || creating}
                      onClick={() => setActive((a) => a - 1)}
                    >
                      Back
                    </Button>
                    {active < STEPS.length - 1 ? (
                      <CustomButton
                        fullWidth={false}
                        onClick={() => setActive((a) => a + 1)}
                        disabled={nextDisabled}
                      >
                        Next
                      </CustomButton>
                    ) : (
                      <CustomButton
                        fullWidth={false}
                        onClick={handleCreate}
                        disabled={
                          creating ||
                          !step0Valid ||
                          !stepsValid ||
                          !triggerValid
                        }
                      >
                        {creating ? "Creating…" : "Create integration"}
                      </CustomButton>
                    )}
                  </Box>
                </Paper>
              </>
            )}
          </Box>
        </Box>

        {stepDialog !== null && client && (
          <StepFormDialog
            open
            onClose={() => setStepDialog(null)}
            clientId={client.id}
            nextStageOrder={nextStageOrder}
            initial={editingDraft}
            onSubmit={(values) => {
              if (editingDraft) {
                setDraftSteps((rows) =>
                  rows.map((r) =>
                    r.tempId === editingDraft.tempId
                      ? { ...values, tempId: r.tempId }
                      : r,
                  ),
                );
              } else {
                setDraftSteps((rows) => [
                  ...rows,
                  { ...values, tempId: nextTempId() },
                ]);
              }
              setStepDialog(null);
            }}
          />
        )}
      </ManagementPageShell>
    </Layout>
  );
}
